import type { TFunction } from 'i18next';

export interface MisdirectedDetectionParameters {
  entity?: string;
  recipient?: string;
  real_recipient?: string;
  [key: string]: string | undefined;
}

/**
 * Renders the localized detection-reason sentence by looking up
 * `log.misdirectedDetail.template.<detect_id>` and interpolating the
 * matching parameters. Falls back to an empty string when the template
 * is missing so the UI can decide what to render in that case.
 *
 * The returned string contains user-supplied values (entity, recipient,
 * real_recipient). It is intended for plain-text rendering only via
 * React text nodes. We force `escapeValue: true` here so even if the
 * global i18next interpolation config drifts, this specific call still
 * escapes HTML in the parameters.
 */
export const renderDetectionReason = (
  t: TFunction,
  detectId: string | undefined,
  parameters: MisdirectedDetectionParameters | undefined
): string => {
  if (!detectId) return '';
  const key = `log.misdirectedDetail.template.${detectId}`;
  const params = parameters ?? {};
  const translated = t(key, {
    ...params,
    interpolation: { escapeValue: true },
  });
  // i18next returns the key itself when missing — treat as no template.
  if (translated === key) return '';
  // If any required placeholder leaked through (e.g. backend omitted a
  // parameter), bail rather than render `{{entity}}` literal text to users.
  if (typeof translated === 'string' && /\{\{[^}]+\}\}/.test(translated)) {
    return '';
  }
  return translated as string;
};
